import React, { useState, useEffect} from "react";
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import { convertFromHTML } from "draft-convert";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import axios from "axios";
import "./BusinessProfileEditor.css";
import {useAuth} from "../context/AuthContext";


  // Funkce pro převod RGB na HEX
  const rgbToHex = (rgb) => {
    const result = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(rgb);
    if (result) {
      const r = parseInt(result[1]).toString(16).padStart(2, '0');
      const g = parseInt(result[2]).toString(16).padStart(2, '0');
      const b = parseInt(result[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
    return rgb; // Pokud není RGB, vrátí původní hodnotu
  }
// Funkce pro převod HTML na DraftJS ContentState
const convertHTMLToContentState = (html) => {
  return convertFromHTML({
    htmlToStyle: (nodeName, node, currentStyle) => {
      if (nodeName === "span" || nodeName === "p" || nodeName === "div") {
        if (node.style.color) {
          const hexColor = rgbToHex(node.style.color);
          currentStyle = currentStyle.add(`COLOR-${hexColor}`);
        }
        if (node.style.fontSize) {
          const fontSize = node.style.fontSize.replace("px", "");
          currentStyle = currentStyle.add(`FONTSIZE-${fontSize}px`);
        }
        if (node.style.fontFamily) {
          currentStyle = currentStyle.add(`FONTFAMILY-${node.style.fontFamily}`);
        }
        if (node.style.fontWeight === "bold") {
          currentStyle = currentStyle.add("BOLD");
        }
        if (node.style.fontStyle === "italic") {
          currentStyle = currentStyle.add("ITALIC");
        }
        if (node.style.textDecoration === "underline") {
          currentStyle = currentStyle.add("UNDERLINE");
        }
        if (node.style.textAlign) {
          currentStyle = currentStyle.add(`TEXTALIGN-${node.style.textAlign}`);
        }
        if (node.classList && node.classList.contains('public-DraftStyleDefault-ltr')) {
          currentStyle = currentStyle.add("TEXTALIGN-left");
        }
      }
      return currentStyle;
    },
    htmlToBlock: (nodeName, node) => {
      if (nodeName === "blockquote") {
        return { type: "blockquote", data: {} };
      }
      return undefined;
    },
    htmlToEntity: (nodeName, node, createEntity) => {
      if (nodeName === "a") {
        return createEntity("LINK", "MUTABLE", { url: node.href });
      }
      return null;
    },
  })(html);
};

const BusinessProfileEditor = () => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [description, setDescription] = useState(""); // Používáme updateBusinessProfile pro počáteční stav
  const [message, setMessage] = useState(null)
  const {businessToken, business, fetchBusiness} = useAuth()

  const styleMap = {
    'COLOR-#000000': { color: '#000000' }, // Černá
    'COLOR-#ff0000': { color: '#ff0000' }, // Červená
    'COLOR-#00ff00': { color: '#00ff00' }, // Zelená
    'COLOR-#0000ff': { color: '#0000ff' }, // Modrá
    'COLOR-#ffff00': { color: '#ffff00' }, // Žlutá
    'COLOR-#ff00ff': { color: '#ff00ff' }, // Purpurová
    'COLOR-#00ffff': { color: '#00ffff' }, // Azurová
    'COLOR-#808080': { color: '#808080' }, // Šedá
    'COLOR-#800000': { color: '#800000' }, // Tmavě červená
    'COLOR-#008000': { color: '#008000' }, // Tmavě zelená
    'COLOR-#000080': { color: '#000080' }, // Tmavě modrá
    'COLOR-#ffa500': { color: '#ffa500' }, // Oranžová
    'COLOR-#800080': { color: '#800080' }, // Fialová
    'COLOR-#008080': { color: '#008080' }, // Tyrkysová
    'COLOR-#ffffff': { color: '#ffffff' }, // Bílá
    'COLOR-#2460a1': { color: '#2460a1' }, // Bílá
    'COLOR-#2575fc': { color: '#2575fc' }, // Bílá

    'BOLD': { fontWeight: 'bold' },
    'ITALIC': { fontStyle: 'italic' },
    'UNDERLINE': { textDecoration: 'underline' },

    'FONTSIZE-12px': { fontSize: '12px' },
    'FONTSIZE-16px': { fontSize: '16px' },
    'FONTSIZE-18px': { fontSize: '18px' },
    'FONTSIZE-20px': { fontSize: '20px' },
    'FONTSIZE-24px': { fontSize: '24px' },
    'FONTSIZE-30px': { fontSize: '30px' },
    'FONTSIZE-36px': { fontSize: '36px' },
    'FONTSIZE-48px': { fontSize: '48px' },
    'FONTSIZE-62px': { fontSize: '62px' },
    'FONTSIZE-72px': { fontSize: '72px' },
    'FONTSIZE-96px': { fontSize: '96px' },

    'FONTFAMILY-Arial': { fontFamily: 'Arial, sans-serif' },
    'FONTFAMILY-Georgia': { fontFamily: 'Georgia, serif' },
    'FONTFAMILY-Impact': { fontFamily: 'Impact, sans-serif' },
    'FONTFAMILY-Tahoma': { fontFamily: 'Tahoma, sans-serif' },

    'TEXTALIGN-left': { textAlign: 'left', display: "block" },
    'TEXTALIGN-center': { textAlign: 'center', display: "block"},
    'TEXTALIGN-right': { textAlign: 'right', display: "block" },
    'TEXTALIGN-justify': { textAlign: 'justify', display: "block" },

    'LINK': { color: 'blue', textDecoration: 'underline' },
};


 
 // Sanitizace HTML pro zamezení problémů s nevalidními značkami
const sanitizeHTML = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.innerHTML;
};



// Načítání a nastavování editorState pouze při změně popisu
useEffect(() => {
    if (business.description) {
      try {
        const cleanHTML = sanitizeHTML(business.description);
        const contentState = convertHTMLToContentState(cleanHTML);
        setEditorState(EditorState.createWithContent(contentState));
      } catch (error) {
        console.error("Chyba při konverzi HTML na editorState:", error);
        setEditorState(EditorState.createEmpty());  // Pokud došlo k chybě, nastavíme editor na prázdný
      }
    } else {
      setEditorState(EditorState.createEmpty());  // Pokud je popis prázdný, nastavíme editor na prázdný
    }
  
}, [business.description]);  // Tento efekt bude spuštěn při změně popisu


const handleDescriptionChange = (newEditorState) => {
  setEditorState(newEditorState);

  const currentContent = newEditorState.getCurrentContent();
  if (!currentContent.hasText()) {
    setDescription("")
    return
  }

  const rawContentState = convertToRaw(currentContent);

  const updatedBlocks = rawContentState.blocks.map((block) => {
    const inlineStyleRanges = block.inlineStyleRanges;

    let styleString = '';

    inlineStyleRanges.forEach((range) => {
      const style = styleMap[range.style];
      if (style) {
        const styleKeys = Object.keys(style);
        styleKeys.forEach((key) => {
          styleString += `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}:${style[key]}; `;
        });
      }
    });

    styleString = styleString.trim();


    // Získání existujících dat v block.data
    const existingData = { ...block.data };


    // Rozdělení styleString na jednotlivé styly
    const styleEntries = styleString.split(';').map((style) => style.trim()).filter(Boolean);

    // Filtrování stylů, které nejsou v existingData
    const filteredStyles = styleEntries.filter((style) => {
      const [key] = style.split(':').map((s) => s.trim());
      return !(key in existingData); // Přidáme jen ty styly, které nejsou v existingData
    });

    // Sestavení nového styleString pouze s relevantními styly
    const newStyleString = filteredStyles.join('; ');

    return {
      ...block,
      data: {
        ...existingData,
        ...(newStyleString ? { style: newStyleString } : {}), // Přidáme style jen pokud je co přidat
      },
    };
  });

  const updatedRawContentState = {
    ...rawContentState,
    blocks: updatedBlocks,
  };

  const htmlContent = draftToHtml(updatedRawContentState);

  setDescription(htmlContent);

};
  // Funkce pro odeslání formuláře
  const formSubmit = async (e) => {
    e.preventDefault();
    const businessId = business._id
    try {
      console.log(description, businessId ,process.env.REACT_APP_API_URL)
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/business/profile/${businessId}/newdescription`,
        { description, businessId }, // Odesíláme description jako JSON objekt
        {
          headers: {
            Authorization: `Bearer ${businessToken}`,
          },
        }
      );
      setMessage(response?.data.message || "Popis aktualizován");
    } catch (err) {
      setMessage(err.response?.data?.message || "Nastala chyba při aktualizaci profilu.");
    } finally {
      setTimeout(() => {
        setMessage(null)
        fetchBusiness()
      }, 2000);
    }
  };

  return (
<div>

  <p style={{ color: 'red', fontSize: '16px', fontStyle: 'italic' }}>
  POZOR! Překopírovaný text může změnit celkový formát popisu na veřejné stránce.
</p>
  <form onSubmit={formSubmit} className="editor-form">
    <Editor
      editorState={editorState}
      onEditorStateChange={handleDescriptionChange}
      placeholder="Napište popis firmy..."
      toolbar={{
        options: ["inline", "blockType", "fontSize", "fontFamily", "textAlign", "colorPicker", "link"],
        inline: { options: ["bold", "italic", "underline", "strikethrough", "monospace"] },
        fontFamily: { options: ["Arial", "Georgia", "Impact", "Tahoma"] },
        colorPicker: { colors: [
          "#000000", "#ff0000", "#00ff00", "#0000ff", "#ffff00", 
          "#ff00ff", "#00ffff", "#808080", "#800000", "#008000", 
          "#000080", "#ffa500", "#800080", "#008080", "#ffffff",
          "#2575fc", "#2460a1"
        ] },
      }}
      customStyleMap={styleMap}
    />
    <button type="submit" className="profile-button">Přidat popis</button>
  </form>
  {message && <p className="message">{message}</p>}
</div>
  );
};

export default BusinessProfileEditor;